/* Monthly picks (ADR 0034 · 0037): the one-row auto slider + the ad dialog.
   Slider: previous / next buttons, a visible Pause / Play button (WCAG 2.2.2),
   one ad every 4 s, back to the start after the last. It holds while the
   pointer is over the row, while the section has keyboard focus, while the
   dialog is open, while the section is off screen and while the tab is hidden.
   Visitors who ask for reduced motion start paused and get instant scrolling.
   Dialog: native <dialog> — Esc closes, focus is trapped and returned to the
   tile; arrow keys move previous / next while it is open.
   Without JS the row still scrolls sideways and a tile links to the image. */
(() => {
  const DELAY = 4000;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (root.dataset.picksReady) return;
    root.dataset.picksReady = '1';

    const dataEl = root.querySelector('[data-picks-data]');
    const dlg = root.querySelector('[data-picks-dialog]');
    if (!dataEl || !dlg) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    /* ── Slider state (the dialog also holds it) ───────────────────────── */
    const track = root.querySelector('.jayone-picks__track');
    const back = root.querySelector('[data-picks-back]');
    const forward = root.querySelector('[data-picks-forward]');
    const toggle = root.querySelector('[data-picks-toggle]');
    const slider = Boolean(track && back && forward && toggle);
    const holds = new Set();
    let paused = root.dataset.autoplay !== 'true' || reduceMotion();
    let timer = null;

    const behavior = () => (reduceMotion() ? 'auto' : 'smooth');
    const stepSize = () => {
      const items = track.children;
      if (items.length < 2) return track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    };
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    const atStart = () => track.scrollLeft <= 4;
    const go = (dir) => {
      if (dir > 0 && atEnd()) track.scrollTo({ left: 0, behavior: behavior() });
      else if (dir < 0 && atStart()) track.scrollTo({ left: track.scrollWidth, behavior: behavior() });
      else track.scrollBy({ left: dir * stepSize(), behavior: behavior() });
    };
    const restart = () => {
      clearInterval(timer);
      timer = null;
      if (slider && !paused && holds.size === 0) timer = setInterval(() => go(1), DELAY);
    };
    const hold = (reason, on) => {
      if (on) holds.add(reason); else holds.delete(reason);
      restart();
    };

    /* ── Dialog ─────────────────────────────────────────────────────────── */
    const img = dlg.querySelector('[data-pick-img]');
    const month = dlg.querySelector('[data-pick-month]');
    const caption = dlg.querySelector('[data-pick-caption]');
    const wrap = dlg.querySelector('[data-pick-products-wrap]');
    const list = dlg.querySelector('[data-pick-products]');
    const pos = dlg.querySelector('[data-pick-pos]');
    const prev = dlg.querySelector('[data-picks-prev]');
    const next = dlg.querySelector('[data-picks-next]');
    const close = dlg.querySelector('[data-picks-close]');
    let index = 0;
    let opener = null;

    const render = () => {
      const d = data[index];
      img.removeAttribute('src');
      img.width = d.w;
      img.height = d.h;
      img.srcset = d.srcset;
      img.sizes = '(min-width: 750px) 60vw, 100vw';
      img.src = d.src;
      img.alt = d.alt;
      month.textContent = d.month;
      caption.textContent = d.caption;
      list.replaceChildren(...d.products.map((p) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'j1-link';
        a.href = p.u;
        a.textContent = p.t;
        li.append(a);
        return li;
      }));
      wrap.hidden = d.products.length === 0;
      pos.textContent = `${index + 1} of ${data.length}`;
    };

    const open = (n, from) => {
      if (typeof dlg.showModal !== 'function') return false;
      index = n;
      opener = from;
      render();
      dlg.showModal();
      document.documentElement.classList.add('jayone-picks-open');
      hold('dialog', true);
      close.focus();
      return true;
    };
    const step = (delta) => {
      index = (index + delta + data.length) % data.length;
      render();
    };

    root.querySelectorAll('[data-pick]').forEach((tile) => {
      tile.addEventListener('click', (e) => {
        if (open(Number(tile.dataset.pick), tile)) e.preventDefault();
      });
    });
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    close.addEventListener('click', () => dlg.close());
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });
    // A click on the backdrop lands on the <dialog> element itself.
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', () => {
      document.documentElement.classList.remove('jayone-picks-open');
      hold('dialog', false);
      if (opener) opener.focus();
    });

    /* ── Slider controls ────────────────────────────────────────────────── */
    if (!slider) return;
    const showState = () => {
      root.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? 'Play automatic scrolling' : 'Pause automatic scrolling');
    };

    back.addEventListener('click', () => { go(-1); restart(); });
    forward.addEventListener('click', () => { go(1); restart(); });
    toggle.addEventListener('click', () => { paused = !paused; showState(); restart(); });

    // Hover and keyboard focus hold the row still; touching or wheeling restarts the wait.
    track.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold('hover', true); });
    track.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hold('hover', false); });
    // Keyboard focus only: a mouse click also focuses a button, and that must not freeze the row.
    root.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) hold('focus', true); });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) hold('focus', false); });
    ['touchstart', 'wheel'].forEach((ev) => track.addEventListener(ev, restart, { passive: true }));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => hold('offscreen', !entry.isIntersecting), { threshold: 0.25 }).observe(track);
    }
    document.addEventListener('visibilitychange', () => hold('hidden', document.hidden));

    root.classList.add('is-ready'); // shows the controls (they do nothing without JS)
    showState();
    restart();
  };

  const boot = () => document.querySelectorAll('[data-picks]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
